import type { KindReason, MessageKind } from "@kwp/contracts";
import type { IconName } from "../../ui/icons";

/**
 * Message kinds in the inbox (architecture §6.7, WP-061, WP-064, WP-065): the inbox opens on All
 * mail, every kind; each kind has its own list (`?kind=…`) from the sidebar, People `?kind=person`.
 */
export const KIND_VIEWS = [
  { key: "person", label: "People", icon: "person" },
  { key: "notification", label: "Notifications", icon: "bell" },
  { key: "newsletter", label: "Newsletters", icon: "newsletter" },
  { key: "receipt", label: "Receipts", icon: "receipt" },
] as const satisfies ReadonlyArray<{ key: MessageKind; label: string; icon: IconName }>;

export type MailKindView = MessageKind | "all";

/** The `kind` URL values that are message kinds (the others narrow to mail, chats or tasks, WP-054). */
const MAIL_KINDS = new Set<string>(["person", "notification", "newsletter", "receipt", "all"]);

/**
 * Which mail the inbox lists for a `kind` value: All mail unless it names a kind (WP-065; an old
 * `kind=all` link is All mail too).
 */
export function mailKindOf(kind: string | undefined): MailKindView {
  return kind !== undefined && MAIL_KINDS.has(kind) ? (kind as MailKindView) : "all";
}

const viewOf = (kind: MessageKind) => KIND_VIEWS.find((k) => k.key === kind) as (typeof KIND_VIEWS)[number];

export function kindLabel(kind: MailKindView): string {
  return kind === "all" ? "All mail" : viewOf(kind).label;
}

const REASONS: Record<KindReason, string> = {
  override: "You chose where mail from this sender goes.",
  "override-domain": "You chose where mail from this sender's domain goes.",
  suggested: "Claude moved this sender here; you can undo it or keep it.",
  app: "It comes from an app KitWork+ knows.",
  "list-id": "It was sent to a mailing list (it has a List-Id header).",
  "list-unsubscribe": "It offers an unsubscribe link (a List-Unsubscribe header), as bulk mail does.",
  "feedback-id": "It carries a bulk sender's Feedback-ID header.",
  "auto-submitted": "It says it was sent automatically (an Auto-Submitted header).",
  precedence: "It is marked as bulk or automatic mail (a Precedence header).",
  "automated-address": "It comes from an address nobody reads (noreply, notifications, mailer-daemon).",
  "gmail-promotions": "Gmail put it in Promotions.",
  "gmail-updates": "Gmail put it in Updates.",
  "gmail-social": "Gmail put it in Social.",
  "gmail-forums": "Gmail put it in Forums.",
  "outlook-other": "Outlook put it in Other, not Focused.",
  correspondent: "You have written to this sender from this account.",
  default: "Nothing marks it as automated, so it counts as from a person. You have not written to this sender: tell KitWork+ what it is.",
};

/** "Why here?": the reason in words. */
export function kindReasonText(reason: KindReason): string {
  return REASONS[reason];
}
